import { defineStore } from 'pinia'
import { ref } from 'vue'

export type InconclusiveEntry = {
  questionId: number
  subtestId: number
  questionIndex: number
  recordedAt: number
  lateTranscriptReceived: boolean
  subtestUnansweredDocked: boolean
}

export type NewInconclusiveEntry = Omit<
  InconclusiveEntry,
  'recordedAt' | 'lateTranscriptReceived' | 'subtestUnansweredDocked'
>

const MAX_TRACKED_ENTRIES = 30

export const useInconclusiveTrackerStore = defineStore('inconclusiveTracker', () => {
  const entries = ref<InconclusiveEntry[]>([])

  const recordInconclusive = (entry: NewInconclusiveEntry) => {
    entries.value.push({
      ...entry,
      recordedAt: Date.now(),
      lateTranscriptReceived: false,
      subtestUnansweredDocked: false
    })
    if (entries.value.length > MAX_TRACKED_ENTRIES) {
      entries.value.splice(0, entries.value.length - MAX_TRACKED_ENTRIES)
    }
  }

  const findLatestByQuestionId = (questionId: number): InconclusiveEntry | undefined => {
    for (let i = entries.value.length - 1; i >= 0; i -= 1) {
      if (entries.value[i].questionId === questionId) return entries.value[i]
    }
    return undefined
  }

  const markLateTranscript = (questionId: number) => {
    const entry = findLatestByQuestionId(questionId)
    if (entry) entry.lateTranscriptReceived = true
  }

  // Returns true only when this call flipped the dock — caller decrements its count then.
  const markSubtestUnansweredDocked = (questionId: number): boolean => {
    const entry = findLatestByQuestionId(questionId)
    if (!entry || entry.subtestUnansweredDocked) return false
    entry.subtestUnansweredDocked = true
    return true
  }

  const getLast = (n: number): InconclusiveEntry[] => {
    if (n <= 0) return []
    return entries.value.slice(-n)
  }

  const reset = () => {
    entries.value = []
  }

  return {
    entries,
    recordInconclusive,
    markLateTranscript,
    markSubtestUnansweredDocked,
    findLatestByQuestionId,
    getLast,
    reset
  }
})
